import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { submitMentorApplication } from '../services/api';
import { toast } from 'sonner';
import { CheckCircle, Users } from 'lucide-react';

const MentorOnboarding = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    company_email: '',
    mobile_number: '',
    designation: '',
    city: '',
    industry: '',
    years_of_experience: ''
  });

  const industries = [
    "Technology & IT",
    "AI & Data Science",
    "Marketing & Growth",
    "Sales & Revenue",
    "Finance & Accounting",
    "HR & Talent",
    "Operations & Supply Chain",
    "Healthcare & Life Sciences",
    "Manufacturing",
    "Retail & E-commerce",
    "Consulting",
    "Education",
    "Media & Entertainment",
    "Real Estate",
    "Others"
  ];

  const indianCities = [
    "Mumbai",
    "Delhi",
    "Bengaluru",
    "Hyderabad",
    "Chennai",
    "Kolkata",
    "Pune",
    "Ahmedabad",
    "Jaipur",
    "Surat",
    "Lucknow",
    "Kanpur",
    "Nagpur",
    "Indore",
    "Thane",
    "Bhopal",
    "Visakhapatnam",
    "Vadodara",
    "Gurgaon",
    "Noida",
    "Others"
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleIndustryChange = (value) => {
    setFormData({
      ...formData,
      industry: value
    });
  };

  const handleCityChange = (value) => {
    setFormData({
      ...formData,
      city: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const submissionData = {
        ...formData,
        years_of_experience: parseInt(formData.years_of_experience)
      };

      const response = await submitMentorApplication(submissionData);
      toast.success(response.message);
      setSubmitted(true);
      
      // Scroll to success message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      toast.error(error.message || 'Failed to submit application');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen" style={{backgroundColor: '#fffaf1'}}>
        <Helmet>
          <title>Application Submitted - EduSocial Mentor Program</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        
        <div className="container mx-auto px-4 py-20">
          <div className="max-w-2xl mx-auto text-center">
            <div className="mb-8">
              <CheckCircle size={80} style={{color: '#33c375'}} className="mx-auto mb-4" />
              <h1 className="text-4xl font-bold mb-4" style={{color: '#012726'}}>
                Thank You for Applying!
              </h1>
              <p className="text-xl text-gray-600 mb-6">
                We've received your mentor application and will review it within 3-5 business days.
              </p>
              <p className="text-gray-600 mb-8">
                Our team will reach out to you at <strong>{formData.company_email}</strong> with next steps.
              </p>
            </div>

            <Card className="text-left mb-8" style={{borderColor: '#33c375', borderWidth: '2px'}}>
              <CardHeader>
                <CardTitle style={{color: '#012726'}}>What Happens Next?</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{backgroundColor: '#e8f5ee', color: '#33c375'}}>
                      <span className="font-bold">1</span>
                    </div>
                    <div>
                      <h3 className="font-semibold" style={{color: '#012726'}}>Application Review</h3>
                      <p className="text-sm text-gray-600">Our team will review your profile and experience</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{backgroundColor: '#e8f5ee', color: '#33c375'}}>
                      <span className="font-bold">2</span>
                    </div>
                    <div>
                      <h3 className="font-semibold" style={{color: '#012726'}}>Onboarding Call</h3>
                      <p className="text-sm text-gray-600">We'll schedule a brief call to discuss the program</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{backgroundColor: '#e8f5ee', color: '#33c375'}}>
                      <span className="font-bold">3</span>
                    </div>
                    <div>
                      <h3 className="font-semibold" style={{color: '#012726'}}>Start Mentoring</h3>
                      <p className="text-sm text-gray-600">Join 250+ mentors shaping the next generation of talent</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate('/')}
                style={{backgroundColor: '#33c375'}}
                className="text-white"
              >
                Back to Home
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/for-mentors')}
                style={{borderColor: '#012726', color: '#012726', borderWidth: '2px'}}
              >
                Learn More About Program
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{backgroundColor: '#fffaf1'}}>
      <Helmet>
        <title>Apply as a Mentor - Join 250+ Industry Professionals | EduSocial</title>
        <meta name="description" content="Join EduSocial's mentor network and shape the next generation of industry-ready talent. Share your expertise with 10K+ students across India." />
        <meta property="og:title" content="Apply as a Mentor - Join EduSocial's Centre of Excellence" />
        <meta property="og:description" content="Mentor students from leading colleges. Flexible commitment, virtual & in-person opportunities available." />
        <meta property="og:image" content="https://edusocial.co.in/og-mentor.jpg" />
        <link rel="canonical" href="https://edusocial.co.in/mentors-onboarding" />
      </Helmet>

      {/* Hero Section */}
      <section className="pt-32 pb-12 px-4 sm:px-6 lg:px-8" style={{background: 'linear-gradient(135deg, #012726 0%, #014d49 100%)'}}>
        <div className="container mx-auto text-center text-white">
          <Users size={60} className="mx-auto mb-4" style={{color: '#33c375'}} />
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">
            Become a Mentor
          </h1>
          <p className="text-xl text-gray-200 max-w-2xl mx-auto mb-2">
            Join 250+ industry professionals shaping the future of talent
          </p>
          <p className="text-gray-300">
            Takes only 2 minutes to apply
          </p>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto max-w-2xl">
          <Card>
            <CardHeader>
              <CardTitle style={{color: '#012726'}}>Mentor Application Form</CardTitle>
              <CardDescription>
                Fill out the form below and we'll get back to you within 3-5 business days.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Priya Sharma"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="company_email">Company Email ID *</Label>
                  <Input
                    id="company_email"
                    name="company_email"
                    type="email"
                    value={formData.company_email}
                    onChange={handleChange}
                    required
                    placeholder="priya.sharma@company.com"
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label htmlFor="mobile_number">Mobile Number *</Label>
                  <Input
                    id="mobile_number"
                    name="mobile_number"
                    type="tel"
                    value={formData.mobile_number}
                    onChange={handleChange}
                    required
                    placeholder="+91-9876543210"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="designation">Designation *</Label>
                  <Input
                    id="designation"
                    name="designation"
                    value={formData.designation}
                    onChange={handleChange}
                    required
                    placeholder="Senior Software Engineer"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="city">City *</Label>
                  <Select value={formData.city} onValueChange={handleCityChange} required>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select your city" />
                    </SelectTrigger>
                    <SelectContent className="z-[100]">
                      {indianCities.map((city) => (
                        <SelectItem key={city} value={city}>
                          {city}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="industry">Industry *</Label>
                  <Select value={formData.industry} onValueChange={handleIndustryChange} required>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select your industry" />
                    </SelectTrigger>
                    <SelectContent className="z-[100]">
                      {industries.map((industry) => (
                        <SelectItem key={industry} value={industry}>
                          {industry}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="years_of_experience">Years of Experience *</Label>
                  <Input
                    id="years_of_experience"
                    name="years_of_experience"
                    type="number"
                    min="0"
                    max="60"
                    value={formData.years_of_experience}
                    onChange={handleChange}
                    required
                    placeholder="8"
                    className="mt-1"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full text-white text-lg py-6"
                  disabled={loading}
                  style={{backgroundColor: '#33c375'}}
                >
                  {loading ? 'Submitting...' : 'Submit Application'}
                </Button>
              </form>

              <div className="mt-6 pt-6 border-t text-center">
                <p className="text-sm text-gray-600">
                  Questions? Email us at{' '}
                  <a href="mailto:support@wldigital.in" className="font-semibold" style={{color: '#33c375'}}>
                    support@wldigital.in
                  </a>
                  {' '}or call{' '}
                  <a href="tel:+917022376856" className="font-semibold" style={{color: '#33c375'}}>
                    +91-7022376856
                  </a>
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Benefits Section */}
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="text-center">
              <CardContent className="pt-6">
                <div className="text-3xl font-bold mb-2" style={{color: '#33c375'}}>10K+</div>
                <p className="text-gray-600">Students Impacted</p>
              </CardContent>
            </Card>
            <Card className="text-center">
              <CardContent className="pt-6">
                <div className="text-3xl font-bold mb-2" style={{color: '#33c375'}}>250+</div>
                <p className="text-gray-600">Active Mentors</p>
              </CardContent>
            </Card>
            <Card className="text-center">
              <CardContent className="pt-6">
                <div className="text-3xl font-bold mb-2" style={{color: '#33c375'}}>100+</div>
                <p className="text-gray-600">Partner Colleges</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MentorOnboarding;
